import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import PieChart from "./pie-chart";
import { Box, useDarkMode } from "ui-components";
import { DealByStageT } from "ui-io/company-io";
import { useQuery } from "react-query";
import { useCallback, useMemo } from "react";
import { getDealByStage } from "ui-io/queries/get-deal-by-stage";
import { formatLargeNumber } from "ui-lib";
import { Colors } from "@blueprintjs/core";
ChartJS.register(ArcElement, Tooltip, Legend);

function DealBy() {
  const [darkMode] = useDarkMode();
  const connection = useQuery(getDealByStage({ limit: 10 }));
  const dealData: DealByStageT[] = useMemo(() => {
    if (!connection?.data?.length) {
      return [];
    }
    const filteredData = connection.data.filter(
      (item: DealByStageT) => item.stage !== null && item.stage !== "Unknown"
    );
    if (!filteredData.length) {
      return [];
    }
    return filteredData.map((company: DealByStageT) => {
      return {
        stage: company.stage,
        occurance: company.occurance,
        totalAmount: company.totalAmount,
      };
    });
  }, [connection.data]);

  const getChartData = useCallback(
    (dataType = "totalAmount") => {
      return {
        labels: dealData.map((i) => i.stage),
        datasets: [
          {
            data: dealData.map((i) =>
              dataType == "totalAmount" ? i.totalAmount : i.occurance
            ), // Data values
            backgroundColor: [
              "#3b82f6",
              "#60a5fa",
              "#7da1f8",
              "#a78bfa",
              "#7c3aed",
              "#bb86fc",
              "#a56efc",
              "#9f4dc9",
              "#682cbf",
              "#5a3ea0",
            ], // Colors for each section
            borderColor: ["#ffffff"],
            borderWidth: 1,
          },
        ],
      };
    },
    [dealData]
  );

  const getChartOption = useCallback(
    (dataType = "totalAmount") => {
      return {
        responsive: true,
        plugins: {
          legend: {
            position: "right",
            labels: {
              color: darkMode ? Colors.GRAY4 : Colors.GRAY2, // Legend text color
              usePointStyle: true,
              pointStyle: "rectRounded",
            },
          },
          tooltip: {
            callbacks: {
              label: function (tooltipItem: any) {
                return dataType == "totalAmount"
                  ? `£${formatLargeNumber(tooltipItem.raw)}`
                  : `Count ${tooltipItem.raw}`;
              },
            },
          },
        },
        scales: {
          x: {
            display: false, 
          },
          y: {
            display: false,
          },
        },
      };
    },
    [dealData]
  );

  return (
    <Box flexDir="row" gap="xxlarge" mt={"medium"} >
      <Box width={"1/2"}>
        <PieChart
          data={getChartData("totalAmount")}
          options={getChartOption("totalAmount") as ChartOptions<"pie">}
          title={"Deal by Stages"}
        />
      </Box>
      <Box width={"1/2"}>
        <PieChart
          data={getChartData("occurance")}
          options={getChartOption("occurance") as ChartOptions<"pie">}
          title={"Percentage of Deals"}
        />
      </Box>
    </Box>
  );
}

export default DealBy;
